import React, { useMemo, useState } from "react";
import { DateTime } from "luxon";
import useStore from "../../../../context/useStore";
import useManagers from "../../../../hooks/useManagers/useManagers";
import useRole from "../../../../hooks/useRole/useRole";
import useLeads from "../useLeads";
import useLeadFilters from "./useLeadFilters";
import ProducerSelect from "./ProviderSelect";
import { PERIOD_SHORTCUTS } from "./leadPresets";
import {
  construireGroupesProchaineTache,
  OPTION_TACHES_TERMINEES,
} from "../../../../helpers/leadTasksOrder";

const initials = (fullName = "") =>
  fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const AdvancedFiltersPanel = ({ onClose, onExport, isExporting }) => {
  const [state] = useStore();
  const { managers } = useManagers();
  const { myRole } = useRole();
  const { productCategory } = useLeads();
  const { leadsFilters, actions, showExpiredManagers } = useLeadFilters();
  const { updateFilters, toggleShowExpiredManagers } = actions;
  const {
    QUOTATION_PRODUCT_TYPES,
    QUOTE_PRODUCT_TYPES,
    LEAD_STATUS,
    LEAD_TASKS,
    LEAD_TASK_DEFAULT_ORDER,
    ROLES_PER_BO,
  } = state.constants.items;
  const {
    products,
    assignedTo,
    statuses,
    producer,
    beginAt,
    endAt,
    onlyFinalized,
    prochaineTache,
  } = leadsFilters.filters;

  const [roleTab, setRoleTab] = useState("ALL");
  const [personQuery, setPersonQuery] = useState("");

  const excludedProducts = ["AUTO"];
  const productList = (
    productCategory === "DOMI" ? QUOTE_PRODUCT_TYPES : QUOTATION_PRODUCT_TYPES
  ).filter((p) => !excludedProducts.includes(p.id));

  const produitsCibles = products.length
    ? products
    : productList.map((p) => p.id);
  const groupesProchaineTache = construireGroupesProchaineTache(
    LEAD_TASKS,
    LEAD_TASK_DEFAULT_ORDER,
    produitsCibles,
    prochaineTache
  );

  // --- toggles tableau (produits / statuts / personnes) -----------------
  const toggleInArray = (name, value, current) => {
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateFilters({ target: { name, value: next } });
  };

  // --- assignation : aplatissement managers + rôle + recherche ----------
  const enrichedManagers = useMemo(
    () =>
      managers
        .filter((m) => m.roles && m.roles.length > 0)
        .filter((m) =>
          producer ? m.producer && String(producer) === String(m.producer) : true,
        )
        .map((m) => ({
          ...m,
          roleKey: m.roles[0].roleKey,
          isExpired:
            m.contractEndAt &&
            DateTime.fromISO(m.contractEndAt) < DateTime.now(),
        })),
    [managers, producer],
  );

  const roleTabs = [
    { id: "ALL", label: "Tous" },
    ...ROLES_PER_BO.map((r) => ({ id: r.id, label: r.name })),
  ];

  const visiblePeople = enrichedManagers
    .filter((m) => (roleTab === "ALL" ? true : m.roleKey === roleTab))
    .filter((m) => (showExpiredManagers ? true : !m.isExpired))
    .filter((m) =>
      personQuery
        ? m.fullName.toLowerCase().includes(personQuery.toLowerCase())
        : true,
    )
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  const hiddenExpiredCount = enrichedManagers.filter(
    (m) =>
      m.isExpired && (roleTab === "ALL" ? true : m.roleKey === roleTab),
  ).length;

  return (
    <div className="lf-panel">
      <div className="lf-panel-grid">
        {/* Produits */}
        <div className="lf-field">
          <label>Produits</label>
          <div className="lf-optgrid">
            {productList.map((p) => {
              const on = products.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className={"lf-opt" + (on ? " lf-on" : "")}
                  aria-pressed={on}
                  onClick={() => toggleInArray("products", p.id, products)}
                >
                  <i className={`fa fa-${p.icon}`} />
                  <span>{p.id.toUpperCase()}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Statuts */}
        <div className="lf-field">
          <label>Statuts</label>
          <div className="lf-statgrid">
            {LEAD_STATUS.map((s) => {
              const on = statuses.includes(s.id);
              const label =
                s.id === "IN_PROGRESS" ? "En cours" : s.name;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={"lf-opt" + (on ? " lf-on" : "")}
                  aria-pressed={on}
                  onClick={() => toggleInArray("statuses", s.id, statuses)}
                >
                  <i className={`fa fa-${s.icon}`} />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lf-field">
          <label>Prochaine tâche</label>
          <select
            className="lf-select"
            name="prochaineTache"
            value={prochaineTache}
            onChange={(e) =>
              updateFilters({
                target: { name: "prochaineTache", value: e.target.value },
              })
            }
          >
            <option value="">Toutes…</option>
            {groupesProchaineTache.length > 1
              ? groupesProchaineTache.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.taches.map((t) => (
                      <option key={`${g.label}-${t.id}`} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </optgroup>
                ))
              : (groupesProchaineTache[0]?.taches || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            <option value={OPTION_TACHES_TERMINEES.id}>
              {OPTION_TACHES_TERMINEES.name}
            </option>
          </select>
        </div>

        {/* Société partenaire (PRO, hors PARTNER) */}
        {myRole !== "PARTNER" && productCategory === "PRO" && (
          <div className="lf-field">
            <label>Société partenaire</label>
            <ProducerSelect bare selectClassName="lf-select" />
          </div>
        )}

        {/* Options (PRO, hors PARTNER) */}
        {myRole !== "PARTNER" && productCategory === "PRO" && (
          <div className="lf-field">
            <label>Options</label>
            <button
              type="button"
              className={"lf-toggle" + (onlyFinalized ? " lf-on" : "")}
              aria-pressed={onlyFinalized}
              onClick={() =>
                updateFilters({
                  target: { name: "onlyFinalized", value: !onlyFinalized },
                })
              }
            >
              <span className="lf-sw" />
              Uniquement les leads finalisés
            </button>
          </div>
        )}

        {/* Assigné à */}
        <div className="lf-field lf-assignee">
          <label>Assigné à</label>
          <div className="lf-asg-head">
            <div className="lf-roletabs" role="tablist">
              {roleTabs.map((rt) => (
                <button
                  key={rt.id}
                  type="button"
                  role="tab"
                  aria-selected={roleTab === rt.id}
                  className={roleTab === rt.id ? "lf-on" : ""}
                  onClick={() => setRoleTab(rt.id)}
                >
                  {rt.label}
                </button>
              ))}
            </div>
            <div className="lf-asg-search">
              <i className="fa fa-search" />
              <input
                type="text"
                value={personQuery}
                placeholder="Rechercher une personne…"
                onChange={(e) => setPersonQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="lf-people">
            {visiblePeople.length === 0 && (
              <span className="lf-empty">Aucune personne</span>
            )}
            {visiblePeople.map((m) => {
              const on = assignedTo.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  className={"lf-person" + (on ? " lf-on" : "")}
                  aria-pressed={on}
                  onClick={() => toggleInArray("assignedTo", m.id, assignedTo)}
                  title={m.isExpired ? "Contrat expiré" : undefined}
                >
                  <span className="lf-av">{initials(m.fullName)}</span>
                  {m.fullName}
                  {on && (
                    <i
                      className="fa fa-times"
                      style={{ fontSize: 9, marginLeft: 2 }}
                    />
                  )}
                </button>
              );
            })}
            {!showExpiredManagers && hiddenExpiredCount > 0 && (
              <button
                type="button"
                className="lf-expired-link"
                onClick={() => toggleShowExpiredManagers()}
              >
                +{hiddenExpiredCount} expiré(s)
              </button>
            )}
          </div>
        </div>

        {/* Période */}
        <div className="lf-field">
          <label>Période</label>
          <div className="lf-daterow">
            <input
              type="date"
              className="lf-dateinput"
              value={beginAt ? beginAt.substring(0, 10) : ""}
              onChange={(e) =>
                updateFilters({
                  target: {
                    name: "beginAt",
                    value: e.target.value
                      ? DateTime.fromISO(e.target.value).toISO()
                      : "",
                  },
                })
              }
            />
            <span className="lf-arrow">→</span>
            <input
              type="date"
              className="lf-dateinput"
              value={endAt ? endAt.substring(0, 10) : ""}
              onChange={(e) =>
                updateFilters({
                  target: {
                    name: "endAt",
                    value: e.target.value
                      ? DateTime.fromISO(e.target.value).endOf("day").toISO()
                      : "",
                  },
                })
              }
            />
          </div>
          <div className="lf-quick-dates">
            {PERIOD_SHORTCUTS.map((sc) => (
              <button
                key={sc.id}
                type="button"
                className="lf-qd"
                onClick={() => actions.setPeriodShortcut(sc.id)}
              >
                {sc.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lf-panel-foot">
        <button
          type="button"
          className="lf-link"
          onClick={actions.clearAllFilters}
        >
          <i className="fa fa-refresh" />
          Réinitialiser
        </button>
        <button
          type="button"
          className="lf-link"
          onClick={onExport}
          disabled={isExporting}
        >
          <i className="fa fa-download" />
          Exporter tout (JSON)
        </button>
        <span className="lf-right">
          <button type="button" className="lf-btn lf-btn-ghost" onClick={onClose}>
            Fermer
          </button>
          <button type="submit" className="lf-btn lf-btn-primary">
            <i className="fa fa-search" />
            Rechercher
          </button>
        </span>
      </div>
    </div>
  );
};

export default AdvancedFiltersPanel;
